/**
 * Token Usage Progress Component
 *
 * Circular progress indicator displaying token usage percentage
 * with color-coded status (NORMAL, CAUTION, WARNING, CRITICAL).
 */

import { cn } from '@shared/utils/cn';

interface TokenUsageProgressProps {
  used: number;
  budget: number;
  status: 'NORMAL' | 'CAUTION' | 'WARNING' | 'CRITICAL';
  size?: 'sm' | 'md' | 'lg';
}

const statusColors = {
  NORMAL: { stroke: '#22c55e', bg: '#dcfce7' },    // Green
  CAUTION: { stroke: '#eab308', bg: '#fef9c3' },   // Yellow
  WARNING: { stroke: '#f97316', bg: '#ffedd5' },   // Orange
  CRITICAL: { stroke: '#ef4444', bg: '#fee2e2' },  // Red
};

const sizes = {
  sm: { size: 48, strokeWidth: 4, fontSize: 'text-xs' },
  md: { size: 80, strokeWidth: 6, fontSize: 'text-lg' },
  lg: { size: 120, strokeWidth: 8, fontSize: 'text-2xl' },
};

/**
 * Circular progress indicator for token usage
 *
 * @example
 * ```tsx
 * <TokenUsageProgress
 *   used={75000}
 *   budget={200000}
 *   status="NORMAL"
 *   size="md"
 * />
 * ```
 */
export function TokenUsageProgress({
  used,
  budget,
  status,
  size = 'md'
}: TokenUsageProgressProps) {
  const percentage = Math.min((used / budget) * 100, 100);
  const { size: dimensions, strokeWidth, fontSize } = sizes[size];
  const colors = statusColors[status];

  const radius = (dimensions - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={dimensions} height={dimensions} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={dimensions / 2}
          cy={dimensions / 2}
          r={radius}
          fill="none"
          stroke={colors.bg}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={dimensions / 2}
          cy={dimensions / 2}
          r={radius}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <span className={cn('absolute font-semibold', fontSize)}>
        {Math.round(percentage)}%
      </span>
    </div>
  );
}
